import { useState, useEffect, useRef } from 'react';
import { MILES_DOCUMENTATION_URL } from '@miles/shared';
import {
  BookOpen,
  Megaphone,
  X,
  Send,
  ArrowLeft,
  CheckCircle2,
  MessageSquare,
  ChevronRight,
  ExternalLink,
  Gift,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_URL } from '../lib/api';
import { cn } from '../lib/utils';

interface BetaStatusSection {
  title: string;
  items: string[];
}

interface BetaStatusEntry {
  date: string;
  version: string | null;
  sections: BetaStatusSection[];
}

interface BetaStatusData {
  version: string | null;
  pluginVersion: string | null;
  entries: BetaStatusEntry[];
}

type View =
  | 'updates'
  | 'feedback'
  | 'feedback-sent'
  | 'slack'
  | 'slack-sent'
  | 'credits'
  | 'credits-sent';

const STORAGE_KEY = 'miles-beta-last-seen-version';


/** True when the URL points to a post on X/Twitter or LinkedIn. */
function isSharePostUrl(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  let host = '';
  try {
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    host = new URL(candidate).hostname.toLowerCase();
  } catch {
    return false;
  }
  return ['x.com', 'twitter.com', 'linkedin.com'].some(
    (d) => host === d || host.endsWith(`.${d}`),
  );
}

/** Formats an ISO timestamp as a short, friendly date (e.g. "June 25"). */
function formatEligibleDate(iso: string | null): string {
  if (!iso) return 'soon';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
  });
}

/** Official multi-color Slack logo mark (lucide's Slack icon is monochrome). */
function SlackLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 127 127"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80z"
        fill="#E01E5A"
      />
      <path
        d="M33.8 80c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z"
        fill="#E01E5A"
      />
      <path
        d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47z"
        fill="#36C5F0"
      />
      <path
        d="M47 33.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H14c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33z"
        fill="#36C5F0"
      />
      <path
        d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9z"
        fill="#2EB67D"
      />
      <path
        d="M93.2 46.9c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.8 6.5 72.7.6 80 .6c7.3 0 13.2 5.9 13.2 13.2v33.1z"
        fill="#2EB67D"
      />
      <path
        d="M80 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8H80z"
        fill="#ECB22E"
      />
      <path
        d="M80 93.2c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80z"
        fill="#ECB22E"
      />
    </svg>
  );
}

export function BetaWidget() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<View>('updates');
  const [statusData, setStatusData] = useState<BetaStatusData | null>(null);
  const [hasNewUpdates, setHasNewUpdates] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [isRequestingSlack, setIsRequestingSlack] = useState(false);
  const [slackError, setSlackError] = useState<string | null>(null);
  const [postUrl, setPostUrl] = useState('');
  const [isClaimingCredits, setIsClaimingCredits] = useState(false);
  const [creditsError, setCreditsError] = useState<string | null>(null);
  // null = not yet loaded, true = can claim, false = in cooldown
  const [creditEligible, setCreditEligible] = useState<boolean | null>(null);
  const [creditNextEligibleAt, setCreditNextEligibleAt] = useState<string | null>(
    null,
  );
  const [autoOpened, setAutoOpened] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Fetch beta status once auth resolves. The widget renders nothing for
  // unauthenticated visitors (see the render guard below), so skip the
  // request for them too — login/terms pages otherwise pay a wasted fetch.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;

    async function fetchStatus() {
      try {
        const res = await fetch(`${API_URL}/api/v2/cloud/beta-status`, {
          credentials: 'include',
        });
        if (!res.ok) {
          console.warn(`[BetaWidget] Failed to fetch beta status: HTTP ${res.status}`);
          return;
        }
        const data: BetaStatusData = await res.json();
        setStatusData(data);

        if (data.version) {
          const lastSeen = localStorage.getItem(STORAGE_KEY);
          if (lastSeen !== data.version) {
            setHasNewUpdates(true);
          }
        }
      } catch (err) {
        console.warn('[BetaWidget] Failed to fetch beta status:', err);
      }
    }

    fetchStatus();
  }, [authLoading, isAuthenticated]);

  // Auto-open on new updates (once per session)
  useEffect(() => {
    if (hasNewUpdates && isAuthenticated && !autoOpened) {
      setAutoOpened(true);
      setIsOpen(true);
      setView('updates');
    }
  }, [hasNewUpdates, isAuthenticated, autoOpened]);

  // Load Free Credits claim eligibility whenever the credits view opens
  useEffect(() => {
    if (view !== 'credits') return;
    let cancelled = false;
    setCreditEligible(null);
    fetch(`${API_URL}/api/v2/cloud/credit-claim-status`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) {
          // Fail open: let the server be the authority on submit.
          if (!cancelled) setCreditEligible(true);
          return;
        }
        setCreditEligible(data.eligible);
        setCreditNextEligibleAt(data.nextEligibleAt ?? null);
      })
      .catch(() => {
        if (!cancelled) setCreditEligible(true);
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  // Mark as seen when viewing updates
  useEffect(() => {
    if (isOpen && view === 'updates' && statusData?.version && hasNewUpdates) {
      localStorage.setItem(STORAGE_KEY, statusData.version);
      setHasNewUpdates(false);
    }
  }, [isOpen, view, statusData?.version, hasNewUpdates]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Don't render on login/terms pages or when not authenticated
  if (authLoading || !isAuthenticated) return null;

  async function handleSendFeedback() {
    if (!feedbackMessage.trim()) return;
    setIsSending(true);
    setFeedbackError(null);
    try {
      const res = await fetch(`${API_URL}/api/v2/cloud/beta-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: feedbackMessage.trim(),
        }),
      });
      if (res.ok) {
        setView('feedback-sent');
        setFeedbackMessage('');
      } else {
        const body = await res.json().catch(() => ({}));
        setFeedbackError(body.error || 'Failed to send feedback. Please try again.');
      }
    } catch (err) {
      console.warn('[BetaWidget] Feedback submission failed:', err);
      setFeedbackError('Network error. Please check your connection and try again.');
    } finally {
      setIsSending(false);
    }
  }

  async function handleRequestSlack() {
    setIsRequestingSlack(true);
    setSlackError(null);
    try {
      const res = await fetch(`${API_URL}/api/v2/cloud/slack-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) {
        setView('slack-sent');
      } else {
        const body = await res.json().catch(() => ({}));
        setSlackError(body.error || 'Failed to send request. Please try again.');
      }
    } catch (err) {
      console.warn('[BetaWidget] Slack invite request failed:', err);
      setSlackError('Network error. Please check your connection and try again.');
    } finally {
      setIsRequestingSlack(false);
    }
  }

  async function handleClaimCredits() {
    if (!postUrl.trim()) return;
    if (!isSharePostUrl(postUrl)) {
      setCreditsError('Enter a link to a post on X, Twitter, or LinkedIn.');
      return;
    }
    setIsClaimingCredits(true);
    setCreditsError(null);
    try {
      const res = await fetch(`${API_URL}/api/v2/cloud/credit-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ postUrl: postUrl.trim() }),
      });
      if (res.ok) {
        setView('credits-sent');
        setPostUrl('');
      } else if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        setCreditNextEligibleAt(body.nextEligibleAt ?? null);
        setCreditEligible(false);
      } else {
        const body = await res.json().catch(() => ({}));
        setCreditsError(body.error || 'Failed to submit claim. Please try again.');
      }
    } catch (err) {
      console.warn('[BetaWidget] Credit claim failed:', err);
      setCreditsError('Network error. Please check your connection and try again.');
    } finally {
      setIsClaimingCredits(false);
    }
  }

  const hasStatus = statusData && statusData.entries.length > 0;

  return (
    <div ref={widgetRef} className="fixed bottom-3 left-3 z-50">
      {/* Floating card */}
      {isOpen && (
        <div className={cn(
          "absolute bottom-14 left-0 max-w-[calc(100vw-24px)] rounded-xl border border-white/10 bg-[#111] shadow-2xl shadow-black/50 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200 transition-all",
          view === 'feedback'
            ? 'w-[540px] max-h-[calc(100vh-76px)]'
            : 'w-[425px] max-h-[min(690px,calc(100vh-76px))]',
        )}>
          {/* Header */}
          <div className="flex items-start justify-between px-3 py-2.5 border-b border-white/10">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                {view !== 'updates' && (
                  <button
                    onClick={() => setView('updates')}
                    aria-label="Back to updates"
                    className="text-white/50 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </button>
                )}
                <span className="text-[16px] font-semibold text-white">
                  {view === 'updates' && 'Latest Updates'}
                  {view === 'feedback' && 'Send Feedback'}
                  {view === 'feedback-sent' && 'Feedback Sent'}
                  {view === 'slack' && 'Join our Slack'}
                  {view === 'slack-sent' && 'Invite Requested'}
                  {view === 'credits' && 'Free Credits'}
                  {view === 'credits-sent' && 'Claim Submitted'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close"
              className="text-white/40 hover:text-white transition-colors mt-0.5"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {view === 'updates' && (
              <div className="px-3 py-2.5 space-y-4">
                {hasStatus ? (
                  statusData.entries.map((entry, ei) => (
                    <div key={ei}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-[15px] font-semibold text-white">{entry.date}</h3>
                          {ei === 0 && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 font-medium">Latest</span>
                          )}
                        </div>
                        {entry.version && (
                          <span className="text-[13px] text-white/30">{entry.version}</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {entry.sections.map((section, si) => (
                          <div key={si} className="rounded-lg bg-white/[0.03] px-3 py-2">
                            <h4 className="text-[12px] font-medium text-white/50 uppercase tracking-wider mb-1.5">
                              {section.title}
                            </h4>
                            <ul className="space-y-1.5">
                              {section.items.map((item, j) => (
                                <li key={j} className="text-[15px] text-white/70 leading-snug pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-white/20">
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      {ei < statusData.entries.length - 1 && (
                        <div className="border-b border-white/5 mt-4" />
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-[15px] text-white/40 text-center py-6">
                    No updates yet. Check back soon!
                  </p>
                )}
              </div>
            )}

            {view === 'feedback' && (
              <div className="p-4 space-y-3">
                  <textarea
                    id="beta-feedback-message"
                    autoFocus
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Your honest feedback is appreciated! What needs to change for Miles to work for you?"
                    rows={10}
                    maxLength={10000}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[15px] text-white placeholder:text-white/40 focus:outline-none focus:border-white/40 resize-none"
                  />
                {feedbackError && (
                  <p className="text-[15px] text-red-400">{feedbackError}</p>
                )}
                <button
                  onClick={handleSendFeedback}
                  disabled={!feedbackMessage.trim() || isSending}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-[15px] font-medium text-white hover:bg-white/15 transition-colors focus:outline-none focus:border-white/40 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send className="h-3.5 w-3.5" />
                  {isSending ? 'Sending...' : 'Send Feedback'}
                </button>
              </div>
            )}

            {view === 'feedback-sent' && (
              <div className="p-8 flex flex-col items-center text-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                <div>
                  <p className="text-[15px] font-medium text-white">Thank you!</p>
                  <p className="text-[15px] text-white/50 mt-1">
                    Your feedback has been sent.
                  </p>
                </div>
              </div>
            )}

            {view === 'slack' && (
              <div className="p-4 space-y-3">
                <p className="text-[15px] text-white/70 leading-relaxed">
                  Join the Miles community on Slack to share what you're
                  building, get help, and share feedback.
                </p>
                <p className="text-[15px] text-white/70 leading-relaxed">
                  Invites are sent by hand, so request one and we'll get you in.
                </p>
                {slackError && (
                  <p className="text-[15px] text-red-400">{slackError}</p>
                )}
                <button
                  onClick={handleRequestSlack}
                  disabled={isRequestingSlack}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-[15px] font-medium text-white hover:bg-white/15 transition-colors focus:outline-none focus:border-white/40 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <SlackLogo className="h-3.5 w-3.5" />
                  {isRequestingSlack ? 'Requesting...' : 'Request invite'}
                </button>
              </div>
            )}

            {view === 'slack-sent' && (
              <div className="p-8 flex flex-col items-center text-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                <div>
                  <p className="text-[15px] font-medium text-white">
                    Invite requested!
                  </p>
                  <p className="text-[15px] text-white/50 mt-1">
                    We'll send your Slack invite
                    {user?.email ? ` to ${user.email}` : ''} soon.
                  </p>
                </div>
              </div>
            )}

            {view === 'credits' && (
              <div className="p-4 space-y-3">
                <p className="text-[15px] text-white/70 leading-relaxed">
                  Built something with Miles? Share it on X or LinkedIn and tag{' '}
                  <a
                    href="https://x.com/bymilesai"
                    target="_blank"
                    rel="noreferrer"
                    className="text-white underline underline-offset-2 hover:text-white/80"
                  >
                    @bymilesai
                  </a>
                  .
                </p>
                {creditEligible === false ? (
                  <p className="text-[15px] text-white/70 leading-relaxed">
                    You've already claimed your share credits. You can claim
                    again on {formatEligibleDate(creditNextEligibleAt)}.
                  </p>
                ) : creditEligible === true ? (
                  <>
                    <p className="text-[15px] text-white/70 leading-relaxed">
                      Then paste the link to your post below and we'll add 500
                      credits to your account.
                    </p>
                    <input
                      type="url"
                      value={postUrl}
                      onChange={(e) => {
                        setPostUrl(e.target.value);
                        if (creditsError) setCreditsError(null);
                      }}
                      placeholder="Link to your X or LinkedIn post"
                      maxLength={2000}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[15px] text-white placeholder:text-white/40 focus:outline-none focus:border-white/40"
                    />
                    {creditsError && (
                      <p className="text-[15px] text-red-400">{creditsError}</p>
                    )}
                    <button
                      onClick={handleClaimCredits}
                      disabled={!postUrl.trim() || isClaimingCredits}
                      className="w-full flex items-center justify-center gap-2 rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-[15px] font-medium text-white hover:bg-white/15 transition-colors focus:outline-none focus:border-white/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Gift className="h-3.5 w-3.5" />
                      {isClaimingCredits ? 'Submitting...' : 'Claim credits'}
                    </button>
                  </>
                ) : null}
              </div>
            )}

            {view === 'credits-sent' && (
              <div className="p-8 flex flex-col items-center text-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                <div>
                  <p className="text-[15px] font-medium text-white">
                    Claim submitted!
                  </p>
                  <p className="text-[15px] text-white/50 mt-1">
                    We'll verify your post and add 500 credits to your account
                    as soon as possible.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer — action list on updates view */}
          {view === 'updates' && (
            <div className="border-t border-white/10 p-1.5">
              <a
                href={MILES_DOCUMENTATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors"
              >
                <BookOpen className="h-[18px] w-[18px] text-indigo-400 shrink-0" />
                <span className="flex-1 text-left">Read Documentation</span>
                <ExternalLink className="h-4 w-4 text-white/30 shrink-0" />
              </a>
              <button
                onClick={() => {
                  setCreditsError(null);
                  setView('credits');
                }}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors"
              >
                <Gift className="h-[18px] w-[18px] text-emerald-400 shrink-0" />
                <span className="flex-1 text-left">Free credits</span>
                <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />
              </button>
              <button
                onClick={() => setView('feedback')}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors"
              >
                <MessageSquare className="h-[18px] w-[18px] text-sky-400 shrink-0" />
                <span className="flex-1 text-left">Send feedback</span>
                <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />
              </button>
              <button
                onClick={() => {
                  setSlackError(null);
                  setView('slack');
                }}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors"
              >
                <SlackLogo className="h-[18px] w-[18px] shrink-0" />
                <span className="flex-1 text-left">Join our Slack</span>
                <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Floating trigger button */}
      <button
        aria-label="Latest updates and feedback"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setView('updates');
          }
        }}
        className={cn(
          'relative h-8 w-8 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg',
          isOpen
            ? 'bg-white text-black/70'
            : 'bg-white text-black/50 hover:text-black/70',
        )}
      >
        <Megaphone className="h-3.5 w-3.5" />
        {/* New updates dot */}
        {hasNewUpdates && !isOpen && (
          <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-indigo-500 border-2 border-white animate-pulse" />
        )}
      </button>
    </div>
  );
}
